import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NavController } from '@ionic/angular';
import { Observable, Subject, Subscription, combineLatest, from, of, timer } from 'rxjs';
import { catchError, distinctUntilChanged, map, startWith, switchMap, take } from 'rxjs/operators';

import { PERMISSIONS_V2_FEATURE_FLAG } from 'src/constants/feature-flags.constants';
import { PROJECT_AUTHORIZATION_REQUIREMENTS } from 'src/constants/project-authorization.constants';
import { TENANT_AUTHORIZATION_REQUIREMENTS } from 'src/constants/tenant-authorization.constants';
import { Conversation, ListRequest } from 'src/models';
import { AuthService } from 'src/services/auth.service';
import { ProjectAuthorizationService } from 'src/services/project-authorization.service';
import type { ProjectAuthorizationRequirement } from 'src/services/project-authorization.service';
import { TenantAuthorizationService } from 'src/services/tenant-authorization.service';
import type { TenantAuthorizationRequirement } from 'src/services/tenant-authorization.service';

import { ConversationsService } from '../../../services/conversations.service';
import { CommandBarAction, CustomCommandBarService } from '../../../services/custom-command-bar.service';

const CONVERSATION_SEARCH_DEBOUNCE_MS = 250;
const EXACT_TICKET_ID_SEARCH = /^#(-?\d+)$/;

interface NavigationCommandDefinition {
  id: string;
  name: string;
  route: string;
  icon: string;
  keywords: string;
}

interface ProjectSettingsCommandDefinition {
  id: string;
  name: string;
  routeSuffix: string;
  icon: string;
  keywords: string;
  newNavOnly?: boolean;
}

interface PermissionsV2CommandAccess<Requirement> {
  requirement?: Requirement;
  broadRead?: boolean;
}

const NAVIGATION_COMMAND_ACCESS: Record<string, PermissionsV2CommandAccess<TenantAuthorizationRequirement>> = {
  'go-to-automations': { requirement: TENANT_AUTHORIZATION_REQUIREMENTS.MANAGE_AUTOMATIONS },
  'go-to-slack-settings': { requirement: TENANT_AUTHORIZATION_REQUIREMENTS.ACCESS_SLACK_SETTINGS },
  'go-to-tags': { requirement: TENANT_AUTHORIZATION_REQUIREMENTS.MANAGE_TAGS },
  'go-to-templates': { broadRead: true },
  'go-to-surveys': { requirement: TENANT_AUTHORIZATION_REQUIREMENTS.SURVEY_LIBRARY },
};

const WORKSPACE_SETTINGS_COMMAND_ACCESS: Record<
  string,
  PermissionsV2CommandAccess<TenantAuthorizationRequirement>
> = {
  'settings-general': { requirement: TENANT_AUTHORIZATION_REQUIREMENTS.MANAGE_GENERAL_SETTINGS },
  'settings-ai': { requirement: TENANT_AUTHORIZATION_REQUIREMENTS.ACCESS_AI_SETTINGS },
  'settings-security': { requirement: TENANT_AUTHORIZATION_REQUIREMENTS.MANAGE_SECURITY },
  'settings-audit-log': { requirement: TENANT_AUTHORIZATION_REQUIREMENTS.VIEW_AUDIT_LOGS },
  'settings-assignment-steps': { requirement: TENANT_AUTHORIZATION_REQUIREMENTS.MANAGE_TENANT_SETTINGS },
  'settings-apps': { requirement: TENANT_AUTHORIZATION_REQUIREMENTS.MANAGE_INTEGRATIONS },
  'settings-admin': { requirement: TENANT_AUTHORIZATION_REQUIREMENTS.ACCESS_ACCOUNT_ADMIN },
  'settings-service-accounts': { requirement: TENANT_AUTHORIZATION_REQUIREMENTS.MANAGE_SERVICE_ACCOUNTS },
  'settings-webhooks': { requirement: TENANT_AUTHORIZATION_REQUIREMENTS.MANAGE_INTEGRATIONS },
  'settings-microsoft-teams': { requirement: TENANT_AUTHORIZATION_REQUIREMENTS.MANAGE_INTEGRATIONS },
  'settings-jsm': { requirement: TENANT_AUTHORIZATION_REQUIREMENTS.MANAGE_INTEGRATIONS },
  'settings-portals': { requirement: TENANT_AUTHORIZATION_REQUIREMENTS.ACCESS_PORTAL_SETTINGS },
  'settings-collaborator-types': { requirement: TENANT_AUTHORIZATION_REQUIREMENTS.MANAGE_TENANT_SETTINGS },
  'settings-project-list': { requirement: TENANT_AUTHORIZATION_REQUIREMENTS.ACCESS_PROJECT_LIST },
};

const PROJECT_SETTINGS_COMMAND_ACCESS: Record<
  string,
  PermissionsV2CommandAccess<ProjectAuthorizationRequirement>
> = {
  'settings-project-settings': { requirement: PROJECT_AUTHORIZATION_REQUIREMENTS.PROJECT_SETTINGS_PAGE },
  'settings-project-members': { requirement: PROJECT_AUTHORIZATION_REQUIREMENTS.PROJECT_MEMBERS_PAGE },
  'settings-project-user-groups': { requirement: PROJECT_AUTHORIZATION_REQUIREMENTS.MANAGE_USER_GROUPS },
  'settings-project-service-accounts': { requirement: PROJECT_AUTHORIZATION_REQUIREMENTS.MANAGE_SERVICE_ACCOUNTS },
  'settings-project-ai': { requirement: PROJECT_AUTHORIZATION_REQUIREMENTS.MANAGE_PROJECTS },
  'settings-project-ticket-types': { requirement: PROJECT_AUTHORIZATION_REQUIREMENTS.CONFIGURE_CONVERSATIONS },
  'settings-project-escalations': { requirement: PROJECT_AUTHORIZATION_REQUIREMENTS.MANAGE_PROJECTS },
  'settings-project-statuses': { requirement: PROJECT_AUTHORIZATION_REQUIREMENTS.CONFIGURE_CONVERSATIONS },
  'settings-project-email': { requirement: PROJECT_AUTHORIZATION_REQUIREMENTS.MANAGE_CHANNEL_SETUP },
  'settings-project-in-app-chat': { requirement: PROJECT_AUTHORIZATION_REQUIREMENTS.MANAGE_CHANNEL_SETUP },
  'settings-project-microsoft-teams': { requirement: PROJECT_AUTHORIZATION_REQUIREMENTS.MANAGE_CHANNEL_SETUP },
  'settings-project-apps': { requirement: PROJECT_AUTHORIZATION_REQUIREMENTS.MANAGE_INTEGRATIONS },
  'settings-project-jsm': { requirement: PROJECT_AUTHORIZATION_REQUIREMENTS.MANAGE_CHANNEL_SETUP },
  'settings-project-mcp-servers': { requirement: PROJECT_AUTHORIZATION_REQUIREMENTS.MANAGE_INTEGRATIONS },
  'settings-project-templates': { broadRead: true },
  'settings-project-surveys': { requirement: PROJECT_AUTHORIZATION_REQUIREMENTS.MANAGE_SURVEYS },
  'settings-project-tags': { requirement: PROJECT_AUTHORIZATION_REQUIREMENTS.MANAGE_TAGS },
  'settings-project-automations': { requirement: PROJECT_AUTHORIZATION_REQUIREMENTS.MANAGE_AUTOMATIONS },
};

const PROJECT_SETTINGS_COMMAND_ROUTES: Record<string, (projectId: string) => string> = {
  'settings-project-templates': (projectId) => `/dashboard/projects/${projectId}/setup/templates`,
  'settings-project-surveys': (projectId) => `/dashboard/projects/${projectId}/setup/surveys`,
  'settings-project-tags': (projectId) => `/dashboard/projects/${projectId}/setup/tags`,
};

/** Project settings pages a template does not have: it cannot receive tickets or connect outside systems. */
const TEMPLATE_HIDDEN_PROJECT_SETTINGS: readonly string[] = [
  'email',
  'in-app-chat',
  'microsoft-teams',
  'service-accounts',
  'apps',
  'jsm',
  'mcp-servers',
];

@Component({
  selector: 'app-custom-command-bar',
  templateUrl: './custom-command-bar.component.html',
  styleUrls: ['./custom-command-bar.component.scss'],
})
export class CustomCommandBarComponent implements OnInit, OnDestroy {
  isOpen$: Observable<boolean>;
  filteredActions: (CommandBarAction & { type?: string; action?: () => void })[] = [];
  displayedActions: CommandBarAction[] = [];
  displayedConversations: (CommandBarAction & { type?: string; action?: () => void })[] = [];
  searchTerm: string = '';
  private searchTermChanged = new Subject<string>();
  private subscriptions = new Subscription();
  activeIndex: number = 0; // For keyboard navigation

  @ViewChild('searchInput', { static: false }) searchInput: ElementRef<HTMLInputElement>;

  @HostListener('document:keydown.escape', ['$event'])
  onKeydownHandler(event: KeyboardEvent) {
    this.customCommandBarService.close();
  }

  constructor(
    public customCommandBarService: CustomCommandBarService,
    private conversationsService: ConversationsService,
    private navController: NavController,
    private elementRef: ElementRef, // For DOM manipulations if needed
    private authService: AuthService,
    private projectAuthorizationService: ProjectAuthorizationService,
    private tenantAuthorizationService: TenantAuthorizationService,
  ) {
    this.isOpen$ = this.customCommandBarService.isOpen$;
  }

  ngOnInit(): void {
    const searchTerm$ = this.searchTermChanged.pipe(
      distinctUntilChanged(),
      map((term) => term.toLowerCase()),
      startWith(''),
    );

    const conversationSearch$: Observable<(CommandBarAction & { type?: string; action?: () => void })[]> =
      searchTerm$.pipe(
        switchMap((term) => {
          if (!term || term.length < 2) {
            return of([]);
          }
          const exactTicketIdMatch = term.trim().match(EXACT_TICKET_ID_SEARCH);
          let listOptions: ListRequest;

          if (exactTicketIdMatch) {
            const friendlyId = Number(exactTicketIdMatch[1]);

            if (!Number.isSafeInteger(friendlyId) || friendlyId < 0) {
              return of([]);
            }

            listOptions = {
              where: [{ field: 'friendlyId', operator: '==', value: friendlyId }],
              limit: 1,
              includeTotalCount: false,
            };
          } else {
            listOptions = {
              search: term,
              limit: 5,
              includeTotalCount: false,
            };
          }

          const conversationRequest$ = timer(CONVERSATION_SEARCH_DEBOUNCE_MS).pipe(
            switchMap(() => from(this.conversationsService.list(listOptions))),
            map((response) => {
              return (response.data || []).map(
                (convo: Partial<Conversation>) =>
                  ({
                    id: convo.id!,
                    name: `[#${convo.friendlyId}] ${convo.title}`,
                    callback: () => {
                      this.navController.navigateRoot(`/dashboard/inbox/all/conversations/${convo.id}`);
                      this.customCommandBarService.close();
                    },
                    type: 'conversation',
                    icon: 'chatbox-ellipses-outline',
                  }) as CommandBarAction & { type?: string; action?: () => void },
              );
            }),
            catchError((error) => {
              console.error('Error fetching conversations:', error);
              return of([]);
            }),
          );

          return exactTicketIdMatch ? conversationRequest$.pipe(startWith([])) : conversationRequest$;
        }),
        startWith([]),
      );

    this.subscriptions.add(
      combineLatest([this.customCommandBarService.actions$, searchTerm$, conversationSearch$]).subscribe(
        ([actions, term, conversationActions]) => {
          const isExactTicketIdSearch = EXACT_TICKET_ID_SEARCH.test(term.trim());

          if (isExactTicketIdSearch) {
            this.displayedActions = [];
          } else if (!term) {
            this.displayedActions = actions.filter((a) => !a.searchOnly).map((a) => ({ ...a }));
          } else {
            this.displayedActions = actions
              .filter(
                (action) =>
                  action.name.toLowerCase().includes(term) ||
                  (action.keywords && action.keywords.toLowerCase().includes(term)),
              )
              .map((a) => ({ ...a }));
          }

          this.displayedConversations = conversationActions;

          this.filteredActions = [...this.displayedActions, ...this.displayedConversations];
          const command = this.searchTerm.trim();
          const canAskTailor = actions.some((action) => action.id === 'open-tailor');

          if (!isExactTicketIdSearch && command && canAskTailor && this.filteredActions.length === 0) {
            this.filteredActions.push({
              id: 'send-to-tailor',
              name: `Ask Tailor: "${command}"`,
              icon: 'custom-tailor-static',
              callback: () => this.customCommandBarService.openTailor(command),
              type: 'tailor',
            });
          }

          this.activeIndex = 0;
        },
      ),
    );

    // Focus the input when the command bar opens
    this.subscriptions.add(
      this.isOpen$.subscribe((isOpen) => {
        if (isOpen) {
          setTimeout(() => {
            this.searchInput?.nativeElement.focus();
            this.searchTermChanged.next('');
            this.searchTerm = '';
          }, 50);
        }
      }),
    );

    // Add "Add filter" action as a global event emitter
    const addFilterAction: CommandBarAction = {
      id: 'add-filter',
      name: 'Add filter...',
      icon: 'filter-outline',
      keywords: 'filter search add find',
      callback: () => {
        // Emit a global event for any filter-button to listen to
        document.dispatchEvent(new CustomEvent('open-filter-popover'));
      },
      section: 'Navigation',
    };
    this.customCommandBarService.registerAction(addFilterAction);

    this.registerNavigationActions();

    this.registerWorkspaceSettingsActions();
    this.registerProjectSettingsActions();
  }

  onSearchTermChange(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchTerm = value;
    this.searchTermChanged.next(value);
  }

  selectAction(item: CommandBarAction & { type?: string; action?: () => void }): void {
    if (item.type === 'conversation' || item.type === 'tailor') {
      if (typeof item.callback === 'function') {
        item.callback();
      }
    } else {
      this.customCommandBarService.triggerAction(item.id);
    }
  }

  formatShortcut(shortcut: CommandBarAction['shortcut']): string {
    if (!shortcut) {
      return '';
    }
    let parts: string[] = [];
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;

    if (shortcut.metaKey) {
      parts.push(isMac ? '⌘' : 'Ctrl');
    } else if (shortcut.ctrlKey) {
      parts.push('Ctrl');
    }
    if (shortcut.altKey) {
      parts.push(isMac ? '⌥' : 'Alt');
    }
    if (shortcut.shiftKey) {
      parts.push(isMac ? '⇧' : 'Shift');
    }
    parts.push(shortcut.key.toUpperCase());
    return parts.join(isMac ? ' ' : '+');
  }

  @HostListener('keydown.arrowdown', ['$event'])
  onArrowDown(event: KeyboardEvent) {
    if (!this.isOpen) {
      return;
    }

    event.preventDefault();

    if (this.activeIndex < this.filteredActions.length - 1) {
      this.activeIndex++;
      this.ensureActiveVisible();
    }
  }

  @HostListener('keydown.arrowup', ['$event'])
  onArrowUp(event: KeyboardEvent) {
    if (!this.isOpen) {
      return;
    }

    event.preventDefault();

    if (this.activeIndex > 0) {
      this.activeIndex--;
      this.ensureActiveVisible();
    }
  }

  @HostListener('keydown.enter', ['$event'])
  onEnter(event: KeyboardEvent) {
    if (!this.isOpen) {
      return;
    }

    event.preventDefault();

    if (this.filteredActions.length > 0 && this.activeIndex >= 0 && this.activeIndex < this.filteredActions.length) {
      this.selectAction(this.filteredActions[this.activeIndex]);
    }
  }

  private get isOpen(): boolean {
    let open = false;
    this.isOpen$.pipe(take(1)).subscribe((val) => (open = val));
    return open;
  }

  private ensureActiveVisible(): void {
    setTimeout(() => {
      const activeItem = this.elementRef.nativeElement.querySelector('.action-item.active');
      if (activeItem) {
        activeItem.scrollIntoView({ block: 'nearest' });
      }
    }, 0);
  }

  private registerWorkspaceSettingsActions(): void {
    const permissionsV2Enabled = this.authService.isFeatureEnabled(PERMISSIONS_V2_FEATURE_FLAG);

    if (!permissionsV2Enabled && !this.authService.isTenantAdmin) {
      return;
    }

    const settingsActions: { id: string; name: string; route: string; icon: string; keywords: string }[] = [
      { id: 'settings-general', name: 'Settings > General', route: 'settings/general', icon: 'settings-outline', keywords: 'general organization org workspace name timezone business hours working hours' },
      { id: 'settings-ai', name: 'Settings > AI', route: 'settings/ai', icon: 'sparkles-outline', keywords: 'ai artificial intelligence llm bot agent knowledge base' },
      { id: 'settings-ai-tester', name: 'Settings > AI Tester', route: 'settings/ai-tester', icon: 'flask-outline', keywords: 'ai test tester playground' },
      { id: 'settings-security', name: 'Settings > Security', route: 'settings/security', icon: 'shield-checkmark-outline', keywords: 'security sso saml authentication login' },
      { id: 'settings-audit-log', name: 'Settings > Audit Log', route: 'settings/audit-log', icon: 'document-text-outline', keywords: 'audit log history activity' },
      { id: 'settings-assignment-steps', name: 'Settings > Assignment Steps', route: 'settings/assignment-steps', icon: 'timer-outline', keywords: 'assignment steps response time support step ruleset' },
      { id: 'settings-apps', name: 'Settings > Apps', route: 'settings/apps', icon: 'apps-outline', keywords: 'apps integrations marketplace' },
      { id: 'settings-admin', name: 'Settings > Admin Account', route: 'settings/admin', icon: 'person-outline', keywords: 'admin account owner billing' },
      { id: 'settings-service-accounts', name: 'Settings > Service Accounts', route: 'settings/service-accounts', icon: 'key-outline', keywords: 'service accounts api keys tokens' },
      { id: 'settings-webhooks', name: 'Settings > Webhooks', route: 'settings/webhooks', icon: 'code-slash-outline', keywords: 'webhooks http callbacks endpoints' },
      { id: 'settings-microsoft-teams', name: 'Settings > Microsoft Teams', route: 'settings/microsoft-teams', icon: 'chatbubbles-outline', keywords: 'microsoft teams ms teams' },
      { id: 'settings-jsm', name: 'Settings > JSM', route: 'settings/jsm', icon: 'git-network-outline', keywords: 'jira jsm service management atlassian' },
      { id: 'settings-portals', name: 'Settings > Portals', route: 'settings/portals', icon: 'globe-outline', keywords: 'portals customer portal internal external' },
      { id: 'settings-collaborator-types', name: 'Settings > Collaborator Types', route: 'settings/collaborator-types', icon: 'people-outline', keywords: 'collaborator types roles agents' },
      { id: 'settings-project-list', name: 'Settings > Project List', route: 'settings/project-list', icon: 'list-outline', keywords: 'projects list manage create' },
    ];
    for (const action of settingsActions) {
      const access = WORKSPACE_SETTINGS_COMMAND_ACCESS[action.id];
      const hasPermissionsV2Access = Boolean(
        access?.requirement && this.tenantAuthorizationService.isAuthorized(access.requirement),
      );

      if (permissionsV2Enabled && !hasPermissionsV2Access) {
        continue;
      }

      this.customCommandBarService.registerAction({
        id: action.id,
        name: action.name,
        icon: action.icon,
        keywords: action.keywords,
        section: 'Navigation',
        searchOnly: true,
        callback: () => {
          this.navController.navigateRoot(`/dashboard/${action.route}`);
          this.customCommandBarService.close();
        },
      });
    }
  }

  private registerNavigationActions(): void {
    const permissionsV2Enabled = this.authService.isFeatureEnabled(PERMISSIONS_V2_FEATURE_FLAG);
    const hasLegacyAccess =
      this.authService.isTenantAdmin ||
      (this.authService.isNewNavEnabled() &&
        this.authService.isProjectOwner(this.authService.mostRecentProject?.id)) ||
      Boolean(
        this.authService.selectedProject &&
          this.authService.isProjectOwner(this.authService.selectedProject.id),
      );
    const navigationActions: NavigationCommandDefinition[] = [
      {
        id: 'go-to-automations',
        name: 'Go to Automations',
        route: '/dashboard/automations',
        icon: 'flash-outline',
        keywords: 'automations workflow rules triggers settings',
      },
      {
        id: 'go-to-tags',
        name: 'Go to Tags',
        route: '/dashboard/settings/tags',
        icon: 'pricetags-outline',
        keywords: 'tags label category settings',
      },
      {
        id: 'go-to-slack-settings',
        name: 'Go to Slack Settings',
        route: '/dashboard/settings/slack',
        icon: 'logo-slack',
        keywords: 'slack integration emoji away status tracking dnd unavailable settings',
      },
      {
        id: 'go-to-templates',
        name: 'Go to Templates',
        route: '/dashboard/outbound/templates',
        icon: 'shapes-outline',
        keywords: 'templates canned responses snippets settings',
      },
      {
        id: 'go-to-surveys',
        name: 'Go to Surveys',
        route: '/dashboard/outbound/surveys',
        icon: 'pie-chart-outline',
        keywords: 'surveys csat nps feedback settings',
      },
    ];

    for (const action of navigationActions) {
      const permissionsV2Access = NAVIGATION_COMMAND_ACCESS[action.id];
      const hasPermissionsV2Access =
        permissionsV2Enabled &&
        (permissionsV2Access?.broadRead === true ||
          Boolean(
            permissionsV2Access?.requirement &&
              this.tenantAuthorizationService.isAuthorized(permissionsV2Access.requirement),
          ));
      const shouldRegister = permissionsV2Enabled ? hasPermissionsV2Access : hasLegacyAccess;

      if (!shouldRegister) {
        continue;
      }

      this.customCommandBarService.registerAction({
        id: action.id,
        name: action.name,
        icon: action.icon,
        keywords: action.keywords,
        section: 'Navigation',
        callback: () => {
          this.navController.navigateRoot(action.route);
          this.customCommandBarService.close();
        },
      });
    }
  }

  private registerProjectSettingsActions(): void {
    const permissionsV2Enabled = this.authService.isFeatureEnabled(PERMISSIONS_V2_FEATURE_FLAG);
    const projectId = permissionsV2Enabled
      ? this.getCurrentProjectId()
      : this.authService.mostRecentProject?.id;
    const hasLegacyAccess = Boolean(projectId && this.authService.isProjectOwner(projectId));

    if (!projectId || (!permissionsV2Enabled && !hasLegacyAccess)) {
      return;
    }

    const isNewNavEnabled = this.authService.isNewNavEnabled();
    const isTemplateProject = this.authService.isTemplateProject(projectId);
    const projectSettingsActions: ProjectSettingsCommandDefinition[] = [
      { id: 'settings-project-settings', name: 'Settings > Project > Settings', routeSuffix: 'settings', icon: 'cog-outline', keywords: 'project settings name description slack emoji away status tracking dnd unavailable' },
      { id: 'settings-project-members', name: 'Settings > Project > Members', routeSuffix: 'members', icon: 'people-outline', keywords: 'project members team users assignees' },
      { id: 'settings-project-user-groups', name: 'Settings > Project > User Groups', routeSuffix: 'user-groups', icon: 'people-circle-outline', keywords: 'user groups teams', newNavOnly: true },
      { id: 'settings-project-ai', name: 'Settings > Project > AI', routeSuffix: 'ai', icon: 'sparkles-outline', keywords: 'project ai bot agent' },
      { id: 'settings-project-ticket-types', name: 'Settings > Project > Ticket Types', routeSuffix: 'ticket-types', icon: 'pricetag-outline', keywords: 'ticket types categories request incident' },
      { id: 'settings-project-escalations', name: 'Settings > Project > Escalations', routeSuffix: 'escalations', icon: 'arrow-up-circle-outline', keywords: 'escalation types priority' },
      { id: 'settings-project-statuses', name: 'Settings > Project > Statuses', routeSuffix: 'statuses', icon: 'pulse-outline', keywords: 'statuses sub-statuses workflow' },
      { id: 'settings-project-email', name: 'Settings > Project > Email', routeSuffix: 'email', icon: 'mail-outline', keywords: 'project email inbound outbound forwarding' },
      { id: 'settings-project-in-app-chat', name: 'Settings > Project > In-App Chat', routeSuffix: 'in-app-chat', icon: 'chatbox-outline', keywords: 'in-app chat widget messenger' },
      { id: 'settings-project-microsoft-teams', name: 'Settings > Project > Microsoft Teams', routeSuffix: 'microsoft-teams', icon: 'chatbubbles-outline', keywords: 'project microsoft teams ms teams channel' },
      { id: 'settings-project-service-accounts', name: 'Settings > Project > Service Accounts', routeSuffix: 'service-accounts', icon: 'key-outline', keywords: 'project service accounts api' },
      { id: 'settings-project-apps', name: 'Settings > Project > Apps', routeSuffix: 'apps', icon: 'apps-outline', keywords: 'project apps integrations' },
      { id: 'settings-project-jsm', name: 'Settings > Project > JSM', routeSuffix: 'jsm', icon: 'git-network-outline', keywords: 'project jira jsm' },
      { id: 'settings-project-mcp-servers', name: 'Settings > Project > Tailor Tools', routeSuffix: 'tailor-tools', icon: 'server-outline', keywords: 'tailor tools copilot mcp servers model context protocol', newNavOnly: true },
      { id: 'settings-project-surveys', name: 'Settings > Project > Surveys', routeSuffix: 'surveys', icon: 'pie-chart-outline', keywords: 'project surveys csat feedback' },
      { id: 'settings-project-templates', name: 'Settings > Project > Templates', routeSuffix: 'templates', icon: 'shapes-outline', keywords: 'project templates canned responses' },
      { id: 'settings-project-tags', name: 'Settings > Project > Tags', routeSuffix: 'tags', icon: 'pricetags-outline', keywords: 'project tags label category' },
      { id: 'settings-project-automations', name: 'Settings > Project > Automations', routeSuffix: 'automations', icon: 'flash-outline', keywords: 'project automations workflow rules triggers' },
    ];

    for (const action of projectSettingsActions) {
      if (action.newNavOnly && !isNewNavEnabled) {
        continue;
      }

      // A template has no channels or integrations, so those pages do not exist for it.
      if (isTemplateProject && TEMPLATE_HIDDEN_PROJECT_SETTINGS.includes(action.routeSuffix)) {
        continue;
      }

      const permissionsV2Access = PROJECT_SETTINGS_COMMAND_ACCESS[action.id];
      const hasPermissionsV2Access =
        permissionsV2Enabled &&
        (permissionsV2Access?.broadRead === true ||
          Boolean(
            permissionsV2Access?.requirement &&
              this.projectAuthorizationService.isAuthorized(projectId, permissionsV2Access.requirement),
          ));
      const shouldRegister = permissionsV2Enabled
        ? hasPermissionsV2Access || (!permissionsV2Access && hasLegacyAccess)
        : hasLegacyAccess;

      if (!shouldRegister) {
        continue;
      }

      this.customCommandBarService.registerAction({
        id: action.id,
        name: action.name,
        icon: action.icon,
        keywords: action.keywords,
        section: 'Navigation',
        searchOnly: true,
        callback: () => {
          const resolvedProjectId = permissionsV2Enabled
            ? projectId
            : this.authService.mostRecentProject?.id;

          if (!resolvedProjectId) {
            this.navController.navigateRoot('/dashboard/settings/project-list');
            this.customCommandBarService.close();

            return;
          }

          const permissionsV2Route = PROJECT_SETTINGS_COMMAND_ROUTES[action.id];

          if (permissionsV2Enabled && permissionsV2Route) {
            this.navController.navigateRoot(permissionsV2Route(resolvedProjectId));
            this.customCommandBarService.close();

            return;
          }

          const base = this.authService.isNewNavEnabled()
            ? `/dashboard/projects/${resolvedProjectId}/setup`
            : `/dashboard/settings/projects/${resolvedProjectId}`;

          this.navController.navigateRoot(`${base}/${action.routeSuffix}`);
          this.customCommandBarService.close();
        },
      });
    }
  }

  private getCurrentProjectId(): string | undefined {
    if (!this.authService.isNewNavEnabled()) {
      return this.authService.selectedProject?.id ?? this.authService.mostRecentProject?.id;
    }

    return this.authService.mostRecentProject?.id;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
